import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const PRODUCTS = [
  {
    id: "stud-bolts",
    num: "01",
    category: "Stud Bolts",
    title: "Pipeline & Engineering Studs",
    grade: "CS · MS · Alloy · Ferritic & Austenitic SS",
    specs: '¼" — 4" (6 – 100 MM) · Any length',
    desc: "Fully threaded headless bolts used on pipeline flanges — tightened from both ends. Also made as engineering studs, both-end threaded with a plain centre.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/63a769fd-a6ef-4e01-90e2-3fadb1316218/images/65dbc15084b48878000ec4c5db34485628bbfa65cef1941748f4437d748d4003.jpeg",
  },
  {
    id: "hex-bolts",
    num: "02",
    category: "Hex Bolts",
    title: "Hex & Heavy Hex Bolts",
    grade: "CS · MS · Alloy · Ferritic & Austenitic SS",
    specs: '¼" — 4" (6 – 100 MM) · ASTM / ASME / BS / DIN',
    desc: "Full or half threaded hexagonal bolts. Heavy hex variants cover more bearing area for higher strength and bearing pressure.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/63a769fd-a6ef-4e01-90e2-3fadb1316218/images/925e6a598796ff1ff91790de2a2e546124d208b03ce955e761d210f25ef25346.jpeg",
  },
  {
    id: "nuts",
    num: "03",
    category: "Nuts",
    title: "Stud Nuts & Hex Nuts",
    grade: "CS · MS · Alloy · Stainless Steel",
    specs: 'M12 — M100 · 1/2" — 4" · UNC / 8UN / UNF / Metric',
    desc: "Threaded fasteners made to pair with our studs and bolts — the product we are best known for. Finished in zinc, HDG, PTFE, black and more.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/63a769fd-a6ef-4e01-90e2-3fadb1316218/images/d98c3012497b30c6773fe386f35a786f9f3ee182d271ee5a52fce5623131b2f4.jpeg",
  },
  {
    id: "countersunk-bolts",
    num: "04",
    category: "Countersunk Bolts",
    title: "Flush-Finish Bolts",
    grade: "MS · Stainless Steel",
    specs: "Flat head · All standard threads",
    desc: "Flat-head bolts that sit flush with the surface for a clean, snag-free finish.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/63a769fd-a6ef-4e01-90e2-3fadb1316218/images/e4f8c6bdb4cbc16488377f37df33728a8a1c1e19ff975d584eed47ff0a0f10f5.jpeg",
  },
  {
    id: "custom-solutions",
    num: "05",
    category: "Customised Engineering Solutions",
    title: "Made to Your Drawing",
    grade: "Any grade · CS · MS · Alloy · SS · Special alloys",
    specs: "Drawing, sample or sketch · Prototype to bulk",
    desc: "Non-standard fasteners and machined components engineered to your specification — special heads, undercut and stepped studs, tie rods, long threaded assemblies and bespoke turned parts. Send a drawing, a sample or even a sketch and we will engineer it, prove the first article and produce it in volume.",
    image:
      "https://static.prod-images.emergentagent.com/jobs/63a769fd-a6ef-4e01-90e2-3fadb1316218/images/e00ea278e7336da504b09040c5b30836b7d1aaeabbb7aaae735559c00648c258.jpeg",
  },
];

export default function Products() {
  const [active, setActive] = useState(0);
  const p = PRODUCTS[active];

  return (
    <section id="products" className="bg-white py-20 lg:py-28" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 lg:mb-16"
        >
          <div>
            <div className="flex items-center gap-3 font-sans font-medium text-[11px] sm:text-xs tracking-[0.12em] uppercase text-steel font-semibold mb-4">
              <span className="w-10 h-[2px] bg-brand" />
              Manufacturing Range
            </div>
            <h2 className="font-display font-extrabold uppercase tracking-tight leading-[1.02] text-4xl sm:text-5xl text-ink">
              Complete fastener range,
              <br />
              manufactured in-house
            </h2>
          </div>
          <p className="max-w-sm text-sm text-slate-600 leading-relaxed">
            Five product lines — from pipeline stud bolts to fully customised
            engineering solutions — manufactured from certified high tensile and
            stainless steel at our Vadodara works.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Index list */}
          <div className="lg:col-span-5 flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0" data-testid="products-tabs">
            {PRODUCTS.map((item, i) => (
              <button
                key={item.id}
                onClick={() => setActive(i)}
                data-testid={`product-tab-${item.id}`}
                className={`group relative shrink-0 lg:shrink text-left border rounded-lg overflow-hidden transition-all duration-500 px-4 lg:px-5 py-3.5 flex items-center gap-4 ${
                  active === i
                    ? "border-navy bg-navy text-white shadow-md"
                    : "border-steel-light bg-white text-navy hover:border-navy/60 hover:shadow-[0_6px_20px_rgba(15,33,55,0.07)] lg:hover:pl-7"
                }`}
              >
                <span
                  className={`absolute left-0 top-0 bottom-0 w-[3px] bg-brand transition-transform duration-500 origin-top ${
                    active === i ? "scale-y-100" : "scale-y-0 group-hover:scale-y-100"
                  }`}
                />
                <span
                  className={`font-sans font-semibold text-[11px] font-medium tracking-widest transition-colors duration-300 ${
                    active === i ? "text-brand" : "text-steel group-hover:text-brand"
                  }`}
                >
                  {item.num}
                </span>
                <span className="font-display font-bold uppercase text-base sm:text-lg leading-none">
                  {item.category}
                </span>
                <ArrowUpRight
                  size={16}
                  className={`ml-auto hidden lg:block transition-all duration-500 ${
                    active === i
                      ? "opacity-100 text-brand translate-x-0"
                      : "opacity-0 -translate-x-2 group-hover:opacity-60 group-hover:translate-x-0"
                  }`}
                />
              </button>
            ))}
          </div>

          {/* Spotlight frame */}
          <div className="lg:col-span-7">
            <div className="relative" data-testid="product-showcase">
              <div
                className="absolute -top-6 -right-2 font-display font-black text-[7rem] leading-none ghost-num select-none hidden sm:block"
                aria-hidden="true"
              >
                {p.num}
              </div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, scale: 0.985, y: 12 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.985, y: -12 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                >
                  <div
                    className="relative overflow-hidden bg-navy-deep aspect-[4/3] rounded-2xl shadow-lg ring-1 ring-slate-900/10"
                    data-testid="product-image-frame"
                  >
                    <motion.img
                      src={p.image}
                      alt={`${p.title} — ${p.category} manufacturer in Vadodara, India | Rahil Engineering Works`}
                      className="absolute inset-0 w-full h-full object-cover"
                      initial={{ scale: 1.08 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 1.2, ease: "easeOut" }}
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/95 via-navy-deep/40 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8 text-white">
                      <div className="font-sans font-medium text-[11px] tracking-[0.12em] uppercase text-blue-200 mb-2">
                        {p.grade}
                      </div>
                      <h3 className="font-display font-extrabold uppercase text-2xl lg:text-3xl leading-tight">
                        {p.title}
                      </h3>
                    </div>
                  </div>
                  <div className="mt-5 flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-8">
                    <p className="text-sm text-slate-600 leading-relaxed max-w-md" data-testid="product-description">
                      {p.desc}
                    </p>
                    <div className="font-sans font-semibold text-[11px] font-medium tracking-[0.06em] text-navy bg-mist border border-steel-light rounded-md px-4 py-2.5 shrink-0">
                      {p.specs}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
